import { type RefObject, useEffect } from 'react'

/**
 * Keep Tab inside a container while it is acting as a modal.
 *
 * `aria-modal` tells a screen reader's virtual cursor to stay put; it does
 * nothing whatever to the Tab key. Without this, tabbing off the last facet in
 * the mobile filter sheet lands on the map behind it - which is covered by the
 * sheet and cannot be seen - so the visitor is then typing at something they
 * have no way to look at.
 *
 * The listener sits on the document rather than the container, so focus that has
 * already escaped (a click on the map behind, a stray `element.focus()`) is
 * pulled back on the next Tab rather than left stranded.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Everything Tab can reach inside `root`, in document order.
 *
 * Filtered on attributes rather than on layout: `offsetParent` and
 * `getBoundingClientRect` are the usual visibility test and both report nothing
 * useful without a layout engine, which would empty this list under jsdom and
 * quietly disable the trap in exactly the tests written to check it.
 */
const focusableWithin = (root: HTMLElement): readonly HTMLElement[] =>
  [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) =>
      element.closest('[hidden]') === null && element.closest('[aria-hidden="true"]') === null,
  )

/**
 * A modal dialog opened on top of this container.
 *
 * While one is up the container is no longer the topmost surface, so it has no
 * business holding Tab: the filter sheet traps focus because everything behind
 * it is covered, and the charge-card editor it opens is in front of it. The
 * editor is portaled to `document.body` - it has to be, or the sheet's `hidden`
 * attribute and its scroll container would both apply to it - so without this
 * the trap would pull focus out of the dialog on the first Tab and leave the
 * form unusable from a keyboard.
 */
const modalAbove = (container: HTMLElement): boolean =>
  [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some(
    (dialog) => dialog !== container && !container.contains(dialog),
  )

export const useFocusTrap = (root: RefObject<HTMLElement | null>, active: boolean): void => {
  useEffect(() => {
    if (!active) return

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return

      const container = root.current
      if (container === null) return
      if (modalAbove(container)) return

      const focusable = focusableWithin(container)
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      // Nothing to hold on to; letting Tab through beats trapping into a void.
      if (first === undefined || last === undefined) return

      const current = document.activeElement
      const inside = current !== null && container.contains(current)

      if (event.shiftKey && (!inside || current === first)) {
        event.preventDefault()
        last.focus()
        return
      }

      if (!event.shiftKey && (!inside || current === last)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [root, active])
}
